import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import i18n from '../../lib/i18n';
import { supportedLanguages, supportedLanguageOptions, calculateEffectiveLanguage, mapSystemLanguageToSupported } from '../../lib/i18n';
import { setLanguagePreference, getSystemLanguage } from '../../utils/languageUtils';
import { updateThemeColorMeta } from '../../utils/themeUtils';

// 主题模式类型
export type ThemeMode = 'light' | 'dark' | 'system';

// ============ 简化的主题管理系统 ============

// 1. 核心状态 - 用户的主题偏好设置
export const themeModeAtom = atomWithStorage<ThemeMode>('shared_theme_mode', 'system');

// 2. 系统主题检测 - 响应式状态
const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export const systemThemeAtom = atom<'light' | 'dark'>(getSystemTheme());

// 系统主题监听和更新机制
export const systemThemeListenerAtom = atom(
  null,
  (get, set) => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleSystemThemeChange = () => {
      const newTheme = getSystemTheme();
      set(systemThemeAtom, newTheme);

      // 如果当前是跟随系统模式，需要重新应用主题
      const currentMode = get(themeModeAtom);
      if (currentMode === 'system') {
        set(themeEffectAtom, { type: 'apply' });
      }
    };

    // 设置监听器
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleSystemThemeChange);
    } else {
      mediaQuery.addListener(handleSystemThemeChange);
    }

    // 返回清理函数
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleSystemThemeChange);
      } else {
        mediaQuery.removeListener(handleSystemThemeChange);
      }
    };
  }
);

// 3. 计算当前是否为暗色主题 - 纯派生状态
export const isDarkAtom = atom<boolean>(get => {
  const mode = get(themeModeAtom);
  const systemTheme = get(systemThemeAtom);
  return mode === 'system' ? systemTheme === 'dark' : mode === 'dark';
});

// 4. 获取当前版本（用于 meta 标签更新）
export const activeVersionAtom = atom<string>(() => {
  if (typeof window === 'undefined') return 'v0';
  return localStorage.getItem('activeVersion') || 'v0';
});

// 5. 主题副作用处理 - 统一处理所有 DOM 操作和副作用
export const themeEffectAtom = atom(
  null,
  (get, set, action: { type: 'apply' } | { type: 'setMode'; mode: ThemeMode }) => {
    if (action.type === 'setMode') {
      // 更新主题模式
      set(themeModeAtom, action.mode);
    }

    // 获取当前状态
    const isDark = get(isDarkAtom);
    const activeVersion = get(activeVersionAtom);

    // 统一应用所有副作用
    if (typeof document !== 'undefined') {
      // 1. 应用 CSS 类
      document.documentElement.classList.toggle('dark', isDark);

      // 2. 更新 meta 标签
      updateThemeColorMeta(isDark ? 'dark' : 'light', activeVersion);

      // 3. 强制重新渲染（触发依赖此状态的组件更新）
      // 通过更新一个时间戳来强制重新渲染
      window.dispatchEvent(new CustomEvent('theme-changed', {
        detail: { isDark, mode: get(themeModeAtom) }
      }));
    }
  }
);

// ============ 兼容性 API（向后兼容现有代码）============

// 兼容旧的 effectiveThemeAtom
export const effectiveThemeAtom = atom<'light' | 'dark'>(get =>
  get(isDarkAtom) ? 'dark' : 'light'
);

// 兼容旧的 darkModeAtom
export const darkModeAtom = atom<boolean>(get => get(isDarkAtom));

// 简化的设置主题函数
export const setThemeModeAtom = atom(
  null,
  (get, set, newThemeMode: ThemeMode) => {
    set(themeEffectAtom, { type: 'setMode', mode: newThemeMode });
  }
);

// 简化的切换主题函数
export const toggleDarkModeAtom = atom(
  null,
  (get, set) => {
    const currentMode = get(themeModeAtom);
    const systemTheme = get(systemThemeAtom);

    let newMode: ThemeMode;
    if (currentMode === 'system') {
      // 如果当前是跟随系统，切换到与系统相反的模式
      newMode = systemTheme === 'dark' ? 'light' : 'dark';
    } else {
      // 在 light 和 dark 之间切换
      newMode = currentMode === 'dark' ? 'light' : 'dark';
    }

    set(themeEffectAtom, { type: 'setMode', mode: newMode });
  }
);

// i18n 相关状态 - 使用可写的 atom 来确保语言变化时能正确更新
export const currentLanguageAtom = atomWithStorage<string>('i18nextLng', 'zh');

// 更新语言的函数（支持 system 选项）
export const changeLanguageAtom = atom(
  null,
  (get, set, language: keyof typeof supportedLanguageOptions) => {
    // 计算有效语言
    const effectiveLanguage = calculateEffectiveLanguage(language);

    // 更新 i18n
    i18n.changeLanguage(effectiveLanguage);
    // 同时更新 atom 状态
    set(currentLanguageAtom, effectiveLanguage);

    // 更新本地存储的用户偏好
    setLanguagePreference(language);
  }
);

// 系统语言监听 atom（当用户选择 system 时使用）
export const systemLanguageWatcherAtom = atom(
  null,
  (get, set) => {
    const handleLanguageChange = () => {
      const userPreference = localStorage.getItem('userLanguagePreference');
      if (userPreference === 'system') {
        const newSystemLanguage = getSystemLanguage();
        const newEffectiveLanguage = mapSystemLanguageToSupported(newSystemLanguage);
        i18n.changeLanguage(newEffectiveLanguage);
        set(currentLanguageAtom, newEffectiveLanguage);
      }
    };

    // 监听语言变化事件
    window.addEventListener('languagechange', handleLanguageChange);

    // 返回清理函数
    return () => {
      window.removeEventListener('languagechange', handleLanguageChange);
    };
  }
);

// 初始化语言状态 - 确保与 i18n 同步
export const initLanguageAtom = atom(
  null,
  (get, set) => {
    const currentLang = i18n.language || 'zh';
    set(currentLanguageAtom, currentLang);

    // 监听 i18n 语言变化事件
    const handleLanguageChange = (lng: string) => {
      set(currentLanguageAtom, lng);
    };

    i18n.on('languageChanged', handleLanguageChange);

    // 返回清理函数
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }
);

// 开发者选项 - Whisper 语音识别限定当前语言（默认关闭）
export const whisperForceCurrentLanguageAtom = atomWithStorage<boolean>(
  'whisper_force_current_language',
  false
);
